/**
 * Deterministic colour palette for default avatar fallbacks.
 *
 * Black-on-black monogram avatars blended into the page bg. This
 * picks a stable, restrained tone for each user. Monograms should remain
 * readable in both themes without turning member lists into a rainbow.
 */

const PALETTE = [
  { bg: 'bg-stone-700', text: 'text-stone-50' },
  { bg: 'bg-slate-700', text: 'text-slate-50' },
  { bg: 'bg-zinc-600', text: 'text-zinc-50' },
  { bg: 'bg-neutral-700', text: 'text-neutral-50' },
  { bg: 'bg-amber-900', text: 'text-amber-50' },
] as const;

function hash(s: string): number {
  // djb2 — fast, stable, evenly distributes over the small palette.
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return Math.abs(h);
}

/**
 * Returns the bg + text Tailwind class pair for the avatar fallback
 * of a given user / channel ID. Same id always returns same colours.
 *
 * Use as: `<div className={`grid ... aether-avatar ${avatarHue(userId).bg} ${avatarHue(userId).text}`}>`
 * The .aether-avatar utility adds the premium glass+rim treatment on top.
 */
export function avatarHue(id: string | null | undefined): { bg: string; text: string } {
  const fallback = PALETTE[0]!;
  if (!id) return fallback;
  const slot = PALETTE[hash(id) % PALETTE.length] ?? fallback;
  return slot;
}
